import React, { createContext, useContext, useState, useCallback } from "react";
import { Modal, Alert, Button } from "antd";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { isSessionError, createSessionError } from "../utils/sessionErrorUtils";

const SessionErrorContext = createContext(null);

export function SessionErrorProvider({ children }) {
  const [sessionError, setSessionError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleSessionError = useCallback((error) => {
    console.error("Session error detected:", error);

    if (isSessionError(error)) {
      console.log("Session error confirmed, showing modal...");
      setSessionError(createSessionError(error));
      setIsModalVisible(true);
      console.log("Modal visibility set to true");
    } else {
      console.log("Error is not a session error, ignoring:", error);
    }
  }, []);

  // Register this handler with the global session error handler
  React.useEffect(() => {
    if (window.setGlobalSessionErrorHandler) {
      window.setGlobalSessionErrorHandler(handleSessionError);
    }

    // Cleanup on unmount
    return () => {
      if (window.setGlobalSessionErrorHandler) {
        window.setGlobalSessionErrorHandler(null);
      }
    };
  }, [handleSessionError]);

  const clearSessionError = useCallback(() => {
    setSessionError(null);
    setIsModalVisible(false);
  }, []);

  const handleReload = useCallback(() => {
    clearSessionError();
    window.location.reload();
  }, [clearSessionError]);

  const handleRedirectToLogin = useCallback(() => {
    clearSessionError();
    window.location.href = "/login";
  }, [clearSessionError]);

  const contextValue = {
    sessionError,
    handleSessionError,
    clearSessionError,
    isSessionError: !!sessionError,
  };

  return (
    <SessionErrorContext.Provider value={contextValue}>
      {children}

      {/* Session Error Modal */}
      {console.log(
        "Rendering modal with isModalVisible:",
        isModalVisible,
        "sessionError:",
        sessionError,
      )}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
            Session Expired
          </div>
        }
        open={isModalVisible}
        onCancel={clearSessionError}
        footer={[
          <Button key="reload" icon={<ReloadOutlined />} onClick={handleReload}>
            Reload Page
          </Button>,
          <Button key="login" type="primary" onClick={handleRedirectToLogin}>
            Go to Login
          </Button>,
        ]}
        closable={false}
        maskClosable={false}
        centered
        width={500}
      >
        <Alert
          message="Session Expired"
          description={
            <div>
              <p>
                {sessionError?.message ||
                  "Your session has expired. Please log in again to continue."}
              </p>
              <p style={{ marginTop: "12px", fontSize: "14px", color: "#666" }}>
                <strong>What you can do:</strong>
              </p>
              <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                <li>Click "Reload Page" to try refreshing your session</li>
                <li>Click "Go to Login" to return to the login page</li>
              </ul>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      </Modal>
    </SessionErrorContext.Provider>
  );
}

export function useSessionError() {
  const context = useContext(SessionErrorContext);
  if (!context) {
    throw new Error(
      "useSessionError must be used within a SessionErrorProvider",
    );
  }
  return context;
}
